// Danh sách thẻ liệu trình trên máy chủ (GET /api/v2/treatment/cards): số dư, cảnh báo, lọc nhanh; bấm dòng mở chi tiết
import { useCallback, useEffect, useRef, useState } from 'react';
import { Empty, Input, Segmented, Select, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import { getBranches } from '../../api/Branch';
import { getTreatmentCards, type CardFlagKey, type CardListItem, type CardListParams, type CardStatusDto, type PackageKind } from '../../api/TreatmentV2';
import { exportExcel, formatPhone } from '../POS/utils';
import { KIND_LABEL } from './data';
import { useTreatmentAuth } from './actions';
import CardDetailApi, { ApiBalanceCell, ApiFlagPills } from './CardDetailApi';
import { SellCardApiModal } from './SellCardApi';
import TreatmentShell, { CARD_STATUS, Pill, fmtDate, money } from './ui';

const WARNINGS: { value: CardFlagKey; label: string }[] = [
  { value: 'low', label: 'Sắp hết thẻ' }, { value: 'expiring', label: 'Sắp hết hạn' }, { value: 'overdue', label: 'Nợ quá hạn' },
  { value: 'due_soon', label: 'Sắp đến hạn đóng' }, { value: 'debt', label: 'Còn công nợ' },
];
const TABS: CardStatusDto[] = ['active', 'expired', 'completed', 'refunded', 'cancelled'];
const PAGE_SIZE = 20;

export default function TreatmentCards() {
  const { can } = useTreatmentAuth();
  const [params, setParams] = useSearchParams();
  const openId = params.get('id');
  const [q, setQ] = useState(params.get('q') || '');
  const [search, setSearch] = useState(q.trim());
  const [status, setStatus] = useState<CardStatusDto | undefined>((params.get('status') as CardStatusDto) || undefined);
  const [flag, setFlag] = useState<CardFlagKey | undefined>((params.get('warn') as CardFlagKey) || undefined);
  const [kind, setKind] = useState<PackageKind | undefined>();
  const [branch, setBranch] = useState<number | undefined>();
  const [branches, setBranches] = useState<{ id: number; name: string }[]>([]);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<CardListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Partial<Record<CardStatusDto, number>>>({});
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selling, setSelling] = useState(false);
  const reqId = useRef(0);
  // Họp 30/10: App Nội khoa xem theo khách hàng — mỗi khách còn bao nhiêu buổi từng dịch vụ, không quản lý tiền ở đây
  const [byCustomer, setByCustomer] = useState(params.get('view') !== 'card');

  useEffect(() => {
    getBranches({ status: 0 }).then((r: any) => setBranches((r?.data?.items || []).map((b: any) => ({ id: Number(b.id), name: b.name }))));
  }, []);

  // Gõ tìm kiếm: chờ 300ms rồi mới gọi máy chủ
  useEffect(() => { const t = setTimeout(() => setSearch(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [search, status, flag, kind, branch]);

  const filters = useCallback((): CardListParams => ({
    search: search || undefined, status, flag, kind, branch_id: branch,
  }), [search, status, flag, kind, branch]);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    const r = await getTreatmentCards({ ...filters(), page, per_page: PAGE_SIZE });
    if (id !== reqId.current) return;
    setLoading(false);
    if (!r.ok || !r.data) { message.error(r.message || 'Không tải được danh sách thẻ'); return; }
    setRows(r.data.items);
    setTotal(r.data.pagination.total);
    setCounts(r.data.tab_counts || {});
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const setOpenId = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('id', id); else next.delete('id');
    setParams(next, { replace: true });
  };

  const columns: ColumnsType<CardListItem> = [
    { key: 'id', title: 'Mã thẻ', width: 110, fixed: 'left', render: (_, r) => <span className="font-mono text-[13px] font-semibold text-[#2c6b7f]">{r.card_code}</span> },
    { key: 'customer', title: 'Khách hàng', width: 180, render: (_, r) => <span><b>{r.customer?.name || '—'}</b>{r.customer?.phone && <div className="text-xs text-slate-500">{formatPhone(r.customer.phone)}</div>}</span> },
    { key: 'pkg', title: 'Gói', width: 220, render: (_, r) => <span>{r.package_name}<div className="text-xs text-slate-400">{KIND_LABEL[r.kind]}{r.branch && ` · ${r.branch.name}`}</div></span> },
    { key: 'balance', title: 'Số dư', width: 170, render: (_, r) => <ApiBalanceCell card={r} /> },
    { key: 'exp', title: 'Hạn dùng', width: 110, render: (_, r) => <span className={r.display_status === 'expired' ? 'font-semibold text-orange-600' : r.flags.expiring ? 'text-amber-700' : ''}>{fmtDate(r.expires_at)}</span> },
    { key: 'paid', title: 'Đã thu / Giá', width: 160, align: 'right', render: (_, r) => <span>{money(r.paid_amount)}<div className="text-xs text-slate-400">/ {money(r.price)}</div></span> },
    { key: 'debt', title: 'Công nợ', width: 120, align: 'right', render: (_, r) => (r.outstanding ? <b className={r.flags.overdue ? 'text-red-600' : 'text-amber-700'}>{money(r.outstanding)}</b> : <span className="text-slate-300">—</span>) },
    { key: 'status', title: 'Trạng thái', width: 120, render: (_, r) => <Pill {...CARD_STATUS[r.display_status]} /> },
    { key: 'flags', title: 'Cảnh báo', width: 230, render: (_, r) => <ApiFlagPills f={r.flags} /> },
  ];

  /** Gom thẻ của trang đang xem theo khách hàng */
  type CustRow = { key: string; name: string; phone: string | null; cards: CardListItem[] };
  const custRows: CustRow[] = Object.values(rows.reduce<Record<string, CustRow>>((m, r) => {
    const k = String(r.customer?.id ?? `card-${r.card_id}`);
    (m[k] ??= { key: k, name: r.customer?.name || '—', phone: r.customer?.phone ?? null, cards: [] }).cards.push(r);
    return m;
  }, {}));
  const left = (c: CardListItem) => (c.kind === 'session' ? `${c.balance.remaining_sessions ?? 0}/${c.balance.total_sessions ?? 0} buổi` : `${money(c.balance.remaining_value ?? 0)} / ${money(c.balance.face_value ?? 0)}`);
  const custCols: ColumnsType<CustRow> = [
    { key: 'code', title: 'Mã KH', width: 100, render: (_, r) => <span className="font-mono text-[13px] font-semibold text-[#2c6b7f]">{r.key.startsWith('card-') ? '—' : `KH-${r.key}`}</span> },
    { key: 'name', title: 'Khách hàng', width: 200, render: (_, r) => <span><b>{r.name}</b>{r.phone && <div className="text-xs text-slate-500">{formatPhone(r.phone)}</div>}</span> },
    { key: 'pkgs', title: 'Gói đang có', render: (_, r) => <span>{r.cards.map((c) => c.package_name).join(' · ')}</span> },
    {
      key: 'left', title: 'Còn lại từng gói', width: 320,
      render: (_, r) => (
        <div className="space-y-0.5">
          {r.cards.map((c) => (
            <button key={c.card_id} type="button" onClick={(e) => { e.stopPropagation(); setOpenId(String(c.card_id)); }} className="block text-left text-[13px] hover:text-[#2c6b7f] hover:underline">
              {c.package_name}: <b>{left(c)}</b> <span className="text-xs text-slate-400">· hạn {fmtDate(c.expires_at)}</span>
            </button>
          ))}
        </div>
      ),
    },
    { key: 'st', title: 'Cảnh báo', width: 200, render: (_, r) => <div className="space-y-1">{r.cards.map((c) => <ApiFlagPills key={c.card_id} f={c.flags} />)}</div> },
  ];

  // Xuất Excel theo bộ lọc hiện tại (tối đa 200 thẻ/lần tải)
  const doExport = async () => {
    setExporting(true);
    const r = await getTreatmentCards({ ...filters(), page: 1, per_page: 200 });
    setExporting(false);
    if (!r.ok || !r.data) { message.error(r.message || 'Không tải được dữ liệu xuất'); return; }
    exportExcel(`the-lieu-trinh-${new Date().toISOString().slice(0, 10)}.xlsx`, 'The lieu trinh', r.data.items.map(c => ({
      'Mã thẻ': c.card_code, 'Khách hàng': c.customer?.name || '', 'SĐT': c.customer?.phone || '', 'Gói': c.package_name, 'Loại thẻ': KIND_LABEL[c.kind],
      'Chi nhánh': c.branch?.name || '',
      'Còn lại': c.kind === 'session' ? `${c.balance.remaining_sessions ?? 0}/${c.balance.total_sessions ?? 0} buổi` : `${c.balance.remaining_value ?? 0}/${c.balance.face_value ?? 0}đ`,
      'Hạn dùng': fmtDate(c.expires_at), 'Giá thẻ': c.price, 'Đã thu': c.paid_amount, 'Công nợ': c.outstanding, 'Trạng thái': CARD_STATUS[c.display_status].label,
      'Đơn POS': c.order?.order_code || '',
    })));
    if (r.data.pagination.total > 200) message.info('Đã xuất 200 thẻ đầu tiên theo bộ lọc');
  };

  return (
    <TreatmentShell
      title="Khách hàng Nội khoa"
      subtitle="Mỗi khách còn bao nhiêu buổi / số dư của từng gói; bấm từng gói xem thanh toán, lịch sử sử dụng, lịch hẹn tiếp."
      onSell={() => setSelling(true)}
      actions={can('export') && <button onClick={doExport} disabled={exporting} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:border-[#3e91ab] disabled:opacity-50"><Download className="h-4 w-4" /> {exporting ? 'Đang xuất…' : 'Excel'}</button>}
    >
      <div className="mb-3 flex flex-wrap gap-2">
        {TABS.map(s => (
          <button key={s} onClick={() => setStatus(status === s ? undefined : s)}
            className={`rounded-full border px-3 py-1 text-sm ${status === s ? 'border-[#357f96] bg-[#357f96] text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-[#3e91ab]'}`}>
            {CARD_STATUS[s].label} <b>{counts[s] ?? 0}</b>
          </button>
        ))}
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input.Search allowClear placeholder="Tên, SĐT, mã thẻ, gói…" value={q} onChange={e => setQ(e.target.value)} className="w-72" />
        <Select allowClear placeholder="Cảnh báo" className="w-44" value={flag} onChange={setFlag} options={WARNINGS} />
        <Select allowClear placeholder="Loại thẻ" className="w-40" value={kind} onChange={setKind} options={[{ value: 'session', label: KIND_LABEL.session }, { value: 'value', label: KIND_LABEL.value }]} />
        <Select allowClear placeholder="Chi nhánh bán" className="w-48" value={branch} onChange={setBranch} options={branches.map(b => ({ value: b.id, label: b.name }))} />
        <Segmented value={byCustomer ? 'cust' : 'card'} onChange={(x) => setByCustomer(x === 'cust')} options={[{ value: 'cust', label: 'Theo khách hàng' }, { value: 'card', label: 'Theo thẻ' }]} />
        <span className="ml-auto text-sm text-slate-500">{total} thẻ</span>
      </div>
      {byCustomer && (
        <Table<CustRow>
          rowKey="key" size="middle" className="custom-ant-table" columns={custCols} dataSource={custRows} loading={loading} scroll={{ x: 'max-content' }}
          pagination={{ current: page, pageSize: PAGE_SIZE, total, hideOnSinglePage: true, showSizeChanger: false, onChange: setPage }}
          locale={{ emptyText: <Empty description="Không có khách phù hợp" /> }}
          onRow={r => ({ onClick: () => setOpenId(String(r.cards[0].card_id)), className: 'cursor-pointer' })}
        />
      )}
      {!byCustomer && <Table<CardListItem>
        rowKey="card_id" size="middle" className="custom-ant-table" columns={columns} dataSource={rows} loading={loading} scroll={{ x: 'max-content' }}
        pagination={{ current: page, pageSize: PAGE_SIZE, total, hideOnSinglePage: true, showSizeChanger: false, onChange: setPage }}
        locale={{ emptyText: <Empty description={search || status || flag || kind || branch ? 'Không có thẻ phù hợp' : 'Chưa có thẻ nào — bấm "Bán thẻ" để phát hành thẻ đầu tiên'} /> }}
        onRow={r => ({ onClick: () => setOpenId(String(r.card_id)), className: 'cursor-pointer' })}
      />}
      <CardDetailApi cardId={openId} onClose={() => setOpenId(null)} onChanged={load} />
      <SellCardApiModal open={selling} onClose={() => setSelling(false)} onDone={id => { load(); setOpenId(String(id)); }} />
    </TreatmentShell>
  );
}
